import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { RefreshCw, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { toast } from "sonner";

export default function RateShopper() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/rate-shopper"); setSnap(data); }
    finally { setLoading(false); }
  }

  async function refresh() {
    setRefreshing(true);
    try { const { data } = await api.post("/rate-shopper/refresh"); setSnap(data); toast.success("Rates refreshed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing(false); }
  }

  useEffect(() => { load(); }, []);

  if (loading || !snap) return <div className="text-white/50">Loading rate shopper…</div>;

  return (
    <div className="space-y-8" data-testid="rate-shopper-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Rate Shopper</div>
          <h1 className="font-display text-3xl md:text-4xl">Competitor intelligence</h1>
          <p className="text-white/55 text-sm mt-2">Live rate comparison with 5 key competitors in your market.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-white/40">Captured: {new Date(snap.captured_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
          <button onClick={refresh} disabled={refreshing} className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="rs-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {["above", "at_par", "below"].map((k) => {
          const count = snap.entries.filter(e => e.position === k).length;
          const label = k === "above" ? "Above market" : k === "below" ? "Below market" : "At par";
          const cls = k === "above" ? "text-emerald-300" : k === "below" ? "text-orange-300" : "text-white/70";
          return (
            <div key={k} className="bg-[#12182B] border border-white/5 rounded-xl p-5" data-testid={`rs-summary-${k}`}>
              <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
              <div className={`font-display text-3xl mt-2 ${cls}`}>{count}</div>
              <div className="text-xs text-white/40">room types</div>
            </div>
          );
        })}
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl overflow-hidden" data-testid="rs-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                <th className="px-5 py-4">Room type</th>
                <th className="px-5 py-4">Your rate</th>
                <th className="px-5 py-4">Market avg</th>
                <th className="px-5 py-4">Delta</th>
                <th className="px-5 py-4">Competitors</th>
              </tr>
            </thead>
            <tbody>
              {snap.entries.map((e) => {
                const Icon = e.position === "above" ? ArrowUp : e.position === "below" ? ArrowDown : Minus;
                const tone = e.position === "above" ? "text-emerald-300" : e.position === "below" ? "text-orange-300" : "text-white/60";
                return (
                  <tr key={e.room_type} className="border-b border-white/5 last:border-none hover:bg-[#1A2235]" data-testid={`rs-row-${e.room_type.replace(/\s/g,'-')}`}>
                    <td className="px-5 py-4 font-medium">{e.room_type}</td>
                    <td className="px-5 py-4 font-display text-base">₹{Math.round(e.your_rate).toLocaleString("en-IN")}</td>
                    <td className="px-5 py-4 text-white/70">₹{Math.round(e.competitor_avg).toLocaleString("en-IN")}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 ${tone}`}>
                        <Icon className="w-3.5 h-3.5" />
                        {e.delta_pct > 0 ? "+" : ""}{e.delta_pct}%
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-2">
                        {e.competitors.map((c) => (
                          <span key={c.competitor} className="text-[11px] bg-white/5 border border-white/10 rounded px-2 py-1">
                            <span className="text-white/60">{c.competitor.split(" ").slice(0, 2).join(" ")}</span>
                            <span className="text-white/90 ml-2">₹{Math.round(c.rate).toLocaleString("en-IN")}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
